import { useEffect } from 'react';
import * as yup from 'yup';
import addDays from 'date-fns/addDays';
import differenceInHours from 'date-fns/differenceInHours';

import {
  TaskImplementationDate,
  TaskNanoID,
  TaskRevisionFields,
  TaskStatuses
} from '../../../../../tasksTypes';
import { RevisionTaskFormData } from '../../RevisionTaskForm.types';

import {
  REVISION_TASK_QUERY,
  RevisionTaskQueryResponse
} from '../../../../../queries/revisionTask.query';

import { useReactHookForm } from '../../../../../../common/hooks/useReactHookForm';
import { useRevisionTask } from '../../../../../hooks/useRevisionTask';

import { dateFnsConvert } from '../../../../../../../utils/dateFnsConvert';

import { ProjectsTaskCache } from '../../../../../../projectsTasks/ProjectsTaskCache';
import { SmartContractCache } from '../../../../../../smartContracts/SmartContractCache';
import { TaskCache } from '../../../../../TaskCache';

import { formsErrors } from '../../../../../../../locales/keys';

const updateImplementationDate = addDays(new Date(), 1).toISOString();

interface RevisionTaskFormOptions {
  taskNanoId: TaskNanoID;
  implementationDate?: TaskImplementationDate;
}

const revisionTaskValidationSchema = yup.object({
  [TaskRevisionFields.IMPLEMENTATION_DATE]: yup
    .string()
    .nullable(true)
    .required(formsErrors.required),
  [TaskRevisionFields.PLANNED_START_DATE]: yup
    .string()
    .nullable(true)
    .when(TaskRevisionFields.STATUS, {
      is: (status: TaskStatuses) => status !== TaskStatuses.IN_PROGRESS,
      then: (schema) =>
        schema
          .required(formsErrors.plannedStartDate.required)
          .test(
            'plannedStartDate',
            formsErrors.plannedStartDate.inFuture,
            (value) => {
              const newDate = new Date(value);
              return newDate > new Date();
            }
          ),
      otherwise: (schema) => schema.notRequired().nullable(true)
    })
});

function useRevisionTaskForm({
  taskNanoId,
  implementationDate
}: RevisionTaskFormOptions) {
  const isChangeImplementationData =
    !implementationDate ||
    differenceInHours(
      new Date(dateFnsConvert.toDateTimeLocal(implementationDate)),
      new Date()
    ) < 12;

  const defaultImplementationDate = implementationDate
    ? implementationDate
    : updateImplementationDate;

  const {
    handleSubmitReactHookForm,
    register,
    errors,
    control,
    resetForm,
    setValue,
    watch
  } = useReactHookForm<RevisionTaskFormData>({
    defaultValues: {
      status: TaskStatuses.IN_PROGRESS,
      feedback: '',
      implementationDate: dateFnsConvert.toDateTimeLocal(
        isChangeImplementationData
          ? updateImplementationDate
          : defaultImplementationDate
      ),
      plannedStartDate: dateFnsConvert.toDateTimeLocal(new Date().toISOString())
    },
    isModalForm: true,
    schema: revisionTaskValidationSchema
  });

  const {
    revisionTask,
    revisionTaskLoading,
    revisionTaskErrorMessage,
    revisionTaskReset
  } = useRevisionTask<RevisionTaskQueryResponse>({
    query: REVISION_TASK_QUERY,
    cacheKeys: [
      ProjectsTaskCache.showProjectTreeCacheKey(),
      SmartContractCache.showCacheKey(),
      TaskCache.showCacheKey(),
      TaskCache.showSidebarCacheKey(),
      TaskCache.showDashboardCacheKey(),
      TaskCache.showHeaderActionsCacheKey(),
      TaskCache.showResultsCacheKey()
    ]
  });

  const watchStatus = watch(TaskRevisionFields.STATUS);

  useEffect(() => {
    const subscription = watch((data, { name }) => {
      if (
        name === TaskRevisionFields.STATUS &&
        data.status !== TaskStatuses.IN_PROGRESS
      ) {
        setValue(
          TaskRevisionFields.PLANNED_START_DATE,
          dateFnsConvert.toDateTimeLocal(new Date().toISOString())
        );
      }
    });

    return () => subscription.unsubscribe();
  }, [setValue, watch]);

  return {
    control,
    revisionTask,
    revisionTaskLoading,
    revisionTaskErrorMessage,
    revisionTaskReset,
    resetForm,
    handleRevisionTask: handleSubmitReactHookForm({
      dirtyFieldsOnly: false,
      onSubmit: async (data: RevisionTaskFormData) =>
        revisionTask({
          uuid: taskNanoId,
          ...data,
          implementationDate: dateFnsConvert.toDateTimeWithTimezone(
            data.implementationDate
          ),
          plannedStartDate:
            data.status === TaskStatuses.IN_PROGRESS
              ? undefined
              : dateFnsConvert.toDateTimeWithTimezone(data.plannedStartDate)
        })
    }),
    isPlannedStartDateVisible: watchStatus !== TaskStatuses.IN_PROGRESS,
    registerImplementationDate: register(
      TaskRevisionFields.IMPLEMENTATION_DATE
    ),
    registerFeedback: register(TaskRevisionFields.FEEDBACK),
    validationErrors: {
      implementationDateValidationError: errors?.implementationDate?.message,
      plannedStartDateValidationError: errors?.plannedStartDate?.message
    },
    registerPlannedStartDate: register(TaskRevisionFields.PLANNED_START_DATE)
  };
}

export default useRevisionTaskForm;
